import { useCallback, useState } from "react";
import type { DraftGroup, DraftSubtask, EmailDraft } from "@/services/inbound-email.service";

/**
 * Local edits of an email draft. Reseeds whenever `sourceKey` changes (another
 * email, or the server returned a newer version after save).
 */
export function useDraftEditor(source: EmailDraft | null, sourceKey: string) {
	const [state, setState] = useState<{ key: string; draft: EmailDraft } | null>(null);

	if (source && state?.key !== sourceKey) {
		setState({ key: sourceKey, draft: structuredClone(source) });
	}

	const draft = state?.key === sourceKey ? state.draft : source;

	const updateGroup = useCallback((gi: number, patch: Partial<DraftGroup>) => {
		setState((s) =>
			s && {
				...s,
				draft: { ...s.draft, groups: s.draft.groups.map((g, i) => (i === gi ? { ...g, ...patch } : g)) },
			},
		);
	}, []);

	const updateSubtask = useCallback((gi: number, si: number, patch: Partial<DraftSubtask>) => {
		setState((s) =>
			s && {
				...s,
				draft: {
					...s.draft,
					groups: s.draft.groups.map((g, i) =>
						i === gi
							? { ...g, subtasks: g.subtasks.map((t, j) => (j === si ? { ...t, ...patch } : t)) }
							: g,
					),
				},
			},
		);
	}, []);

	const reset = useCallback(() => {
		if (source) setState({ key: sourceKey, draft: structuredClone(source) });
	}, [source, sourceKey]);

	return {
		draft,
		updateGroup,
		updateSubtask,
		reset,
		dirty: Boolean(source && draft && JSON.stringify(draft) !== JSON.stringify(source)),
	};
}

export type DraftEditorApi = ReturnType<typeof useDraftEditor>;
